import { useEffect, useMemo } from 'react';
import { useTutorialContext } from '@/contexts/TutorialContext';
import type { DevelopmentStatus, QueryAssetsConnectionArgs } from '@/types/GraphqlSchema';
import { FilterKey } from '../components/Filters/filterConfig';
import { useValuationsStore } from '../store/valuationsStore';
import { useDevelopmentStatus } from './useDevelopmentStatus';
import { useShoreStatus } from './useShoreStatus';

const useFilterOptionsSync = () => {
  const filters = useValuationsStore.use.filters();
  const setFilters = useValuationsStore.use.setFilters();

  const selectedCountries = useMemo(() => {
    const countries = filters[FilterKey.Countries];
    return countries.map(c => c.isoCode);
  }, [filters]);

  const { data } = useDevelopmentStatus(selectedCountries, {
    fetchPolicy: 'cache-and-network',
  });

  useEffect(() => {
    const developmentStatus = data?.developmentStatusWithFilter || [];

    setFilters(prevFilters => {
      const currentStatuses = prevFilters[FilterKey.DevelopmentStatuses];
      const filteredDevelopmentStatus = currentStatuses.filter((status: DevelopmentStatus) =>
        developmentStatus.find((s: DevelopmentStatus) => s.id === status.id && !s.isDisabled),
      );

      return {
        ...prevFilters,
        [FilterKey.DevelopmentStatuses]: filteredDevelopmentStatus,
      };
    });
  }, [data, setFilters]);
};

const parseOptionalFloat = (value: string | undefined | null): number | null => {
  if (!value || value.trim() === '') return null;
  const parsed = Number.parseFloat(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const useShoreStatusWithPreSalt = () => {
  const filters = useValuationsStore.use.filters();
  const { data: shoreStatusData } = useShoreStatus();
  const shoreStatusesFromStore = filters[FilterKey.ShoreStatuses];

  const selectedShoreStatuses = useMemo(() => {
    const statuses = [...shoreStatusesFromStore];

    const hasDeepWater = statuses.some(status => status.name.toLowerCase() === 'deepwater');
    const hasPreSalt = statuses.some(status => status.name.toLowerCase() === 'pre-salt');

    if (hasDeepWater && !hasPreSalt) {
      const preSalt = shoreStatusData?.shoreStatus.find(
        (status: { name: string; id: string }) => status.name.toLowerCase() === 'pre-salt',
      );
      if (preSalt) {
        statuses.push(preSalt);
      }
    }

    return statuses;
  }, [shoreStatusesFromStore, shoreStatusData?.shoreStatus]);

  const selectedShoreStatusesIds = useMemo(
    () => selectedShoreStatuses.map(a => a.id),
    [selectedShoreStatuses],
  );

  return selectedShoreStatusesIds;
};

export const useQueryVariables = (): QueryAssetsConnectionArgs => {
  const { tutorial } = useTutorialContext();
  const shoreStatusesIds = useShoreStatusWithPreSalt();
  useFilterOptionsSync();

  const filters = useValuationsStore.use.filters();
  const sortType = useValuationsStore.use.sortType();
  const sortAscending = useValuationsStore.use.sortAscending();

  const assets = filters[FilterKey.Assets];

  const transformedAssets = useMemo(() => {
    const assetLegacyIds = assets
      .map(asset => asset.legacyId ?? undefined)
      .filter((id): id is string => typeof id === 'string');
    const assetIsoCodes = assets
      .map(asset => asset.country.isoCode ?? undefined)
      .filter((isoCode): isoCode is string => typeof isoCode === 'string');

    return {
      assetLegacyIds,
      assetIsoCodes,
    };
  }, [assets]);

  const variables = useMemo<QueryAssetsConnectionArgs>(() => {
    const countries = filters[FilterKey.Countries];
    const companies = filters[FilterKey.Companies];
    const basins = filters[FilterKey.Basins];
    const geologies = filters[FilterKey.Geologies];
    const hydrocarbons = filters[FilterKey.Hydrocarbons];
    const developmentStatuses = filters[FilterKey.DevelopmentStatuses];
    const developmentTypes = filters[FilterKey.DevelopmentTypes];

    const npvMin = filters[FilterKey.NpvMin];
    const npvMax = filters[FilterKey.NpvMax];
    const reservesMin = filters[FilterKey.ReservesMin];
    const reservesMax = filters[FilterKey.ReservesMax];
    const productionMin = filters[FilterKey.ProductionMin];
    const productionMax = filters[FilterKey.ProductionMax];
    const depthMin = filters[FilterKey.DepthMin];
    const depthMax = filters[FilterKey.DepthMax];
    const includeHistoricalParticipants = filters[FilterKey.IncludeHistoricalParticipants];
    const nonOperator = filters[FilterKey.NonOperator];
    const operator = filters[FilterKey.Operator];
    const opportunities = filters[FilterKey.Opportunities];
    const companyOperand = filters[FilterKey.CompanyOperand];

    return {
      ...transformedAssets,
      country: countries.map(a => a.isoCode),
      company: companies.map(a => a.name),
      companiesByAndOperator: companyOperand === 'AND',
      basin: basins.map(a => a.name),
      shoreStatus: shoreStatusesIds,
      geologyEpochIds:
        geologies?.map(a => a?.epoch?.id).filter((id): id is string => id !== undefined) || [],
      hydrocarbonType: hydrocarbons
        .map(a => a.type)
        .filter((type): type is string => type !== undefined),
      includeHistoricalParticipants,
      oilType: hydrocarbons.map(a => a.oil).filter((oil): oil is string => oil !== undefined),
      npvMin: parseOptionalFloat(npvMin),
      npvMax: parseOptionalFloat(npvMax),
      nonOperator,
      operator,
      opportunities,
      reservesMin: parseOptionalFloat(reservesMin),
      reservesMax: parseOptionalFloat(reservesMax),
      productionMin: parseOptionalFloat(productionMin),
      productionMax: parseOptionalFloat(productionMax),
      developmentStatus: developmentStatuses.map(a => a.id),
      developmentType: developmentTypes.map(a => a.id),
      depthMin: parseOptionalFloat(depthMin),
      depthMax: parseOptionalFloat(depthMax),
      isWalkthrough: tutorial,
      first: 10,
      sortType: sortType.filter,
      sortAscending,
    };
  }, [filters, transformedAssets, shoreStatusesIds, tutorial, sortType.filter, sortAscending]);

  return variables;
};
